/**
 * GLAMRDiP cart sanitizer — Layer 2 anti-abuse for $0 line items.
 * Joel 2026-05-02.
 *
 * Removes any $0 line item that doesn't pass one of these checks:
 *   - properties._free_gift === 'true'  →  validated by the GWP
 *     reconciler (gd-gift-with-purchase.js); this sanitizer leaves it.
 *   - properties._addon === 'true' AND properties.__bundle_id matches
 *     a parent line item with _kit === 'true' AND same __bundle_id  →
 *     legitimate kit-bundle child; leave.
 *   - properties._no_sanitize === 'true'  →  explicit allow-list
 *     escape hatch for future $0 SKUs that aren't kit children or
 *     gifts (free samples sold via a different flow, etc).
 *   - line is NOT $0 → not our concern; leave.
 * Anything else gets removed via /cart/change.js quantity:0.
 *
 * The bundle builder (kit-builder.js, instant-gels-builder.js) uses an
 * ATOMIC `items: [parent, ...addons]` /cart/add.js POST — parent + all
 * addons land in the cart in one transaction. The sanitizer reads cart
 * AFTER that transaction completes so it never sees an addon without
 * its parent.
 *
 * Coordination with gd-gift-with-purchase.js:
 *   - Both modules listen for the same fetch monkey-patch + cart events.
 *   - Both check window.__gdCartProcessing before running. Whichever
 *     fires first sets the flag; the other skips that cycle.
 *   - The flag clears in a try/finally so a thrown error never strands
 *     the cart in "processing" state.
 *
 * Sequencing:
 *   - Sanitizer fires AFTER /cart/add.js / /cart/change.js / /cart/update.js
 *     completes (via shared fetch monkey-patch).
 *   - Bundle builder's atomic add → all items present at sanitize time.
 *   - GWP reconciler reward-add → reward is the $0 line; sanitizer sees
 *     `_free_gift: 'true'` property and skips. No conflict.
 *
 * Failure modes (silent — surface via console.warn):
 *   - /cart.js fetch fails → no sanitize this tick; next event retries.
 *   - /cart/change.js fails for a specific line → log, skip that line,
 *     continue. Doesn't block other removals.
 */
(function () {
  'use strict';

  // Joel 2026-05-03: explicit init log so we can verify the sanitizer is
  // actually loaded + running on every cart-bearing page. Production
  // exploit (order AU2388783) raised a question about whether the
  // sanitizer was firing — this surface makes that easy to confirm in
  // DevTools console without adding any operational overhead.
  console.info('[gd-cart-sanitizer] active');

  // Initialize the shared coordination flag if not already set.
  if (typeof window.__gdCartProcessing !== 'boolean') {
    window.__gdCartProcessing = false;
  }

  let pendingTimer = 0;
  function debouncedSanitize(delay) {
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(() => {
      pendingTimer = 0;
      sanitize();
    }, delay || 200);
  }

  async function sanitize() {
    if (window.__gdCartProcessing) return;
    window.__gdCartProcessing = true;
    try {
      const cart = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }).then((r) => (r.ok ? r.json() : null));
      if (!cart || !Array.isArray(cart.items)) return;

      // Build map: __bundle_id → set of variant ids that have _kit:true
      // (the parent kit lines for each bundle).
      const parentBundles = new Set();
      cart.items.forEach((it) => {
        const props = it.properties || {};
        if (props._kit === 'true' && props.__bundle_id) {
          parentBundles.add(props.__bundle_id);
        }
      });

      const toRemove = [];
      cart.items.forEach((it) => {
        const finalPrice = it.final_price !== undefined ? it.final_price : it.price;
        if (finalPrice !== 0) return; // not $0; out of scope
        const props = it.properties || {};

        // Allow-list: explicit opt-out
        if (props._no_sanitize === 'true') return;

        // Allow-list: validated free gift (GWP reconciler owns this)
        if (props._free_gift === 'true') return;

        // Allow-list: tiered free gift (gd-tier-gifts.js reconciler owns this). Kept like
        // _free_gift. Deliberately NOT added to the qty-force block below — a tier can carry a
        // qty>1 item and the tier reconciler manages the exact quantity, so the sanitizer must
        // never strip it OR reset its qty to 1.
        if (props._gd_gift) return;

        // Allow-list: kit addon with valid parent bundle
        if (props._addon === 'true') {
          if (props.__bundle_id && parentBundles.has(props.__bundle_id)) {
            return; // parent kit present in same cart; legitimate addon
          }
          // Orphan addon — bundle parent missing OR __bundle_id missing
          toRemove.push(it.key);
          return;
        }

        // $0 line with no recognized property — orphan, remove
        toRemove.push(it.key);
      });

      // Joel 2026-05-02: qty-cap enforcement for system-managed lines.
      // Free gifts and kit addons must always be qty=1. Cart drawer +
      // cart page hide their qty steppers in Liquid (Layer 1) but a
      // direct /cart/change.js POST or /cart/update.js POST can still
      // bump the quantity. This catches that within ~200ms (debounced
      // sanitize). Resets to qty=1 instead of removing — preserves the
      // legitimate single line.
      const toResetToOne = [];
      cart.items.forEach((it) => {
        const props = it.properties || {};
        const isFreeGift = props._free_gift === 'true';
        const isAddon = props._addon === 'true';
        if ((isFreeGift || isAddon) && it.quantity > 1) {
          toResetToOne.push(it.key);
        }
      });

      if (toRemove.length === 0 && toResetToOne.length === 0) return;

      // Apply removes one at a time to avoid /cart/change.js race
      // (sequential keys-by-line are stable until the first removal,
      // then re-index — using the stable line key avoids that).
      for (const key of toRemove) {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 }),
          });
        } catch (e) {
          console.warn('[gd-cart-sanitizer] remove failed:', key, e.message);
        }
      }

      // Apply qty-resets after orphan-removes (so the line keys we're
      // about to write to are still valid — orphan-remove changes line
      // indices but key-based addressing remains stable).
      for (const key of toResetToOne) {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: 1 }),
          });
        } catch (e) {
          console.warn('[gd-cart-sanitizer] qty-reset failed:', key, e.message);
        }
      }

      // Notify cart drawer + GWP reconciler that cart state has changed
      document.body.dispatchEvent(new CustomEvent('cart:updated'));
      document.body.dispatchEvent(new CustomEvent('gd:cart-refresh-request'));
      // eslint-disable-next-line no-console
      if (toRemove.length) {
        console.warn(`[gd-cart-sanitizer] removed ${toRemove.length} orphan $0 line(s)`);
      }
      if (toResetToOne.length) {
        console.warn(`[gd-cart-sanitizer] reset qty=1 on ${toResetToOne.length} system-managed line(s)`);
      }
    } catch (e) {
      console.warn('[gd-cart-sanitizer] sanitize error:', e);
    } finally {
      window.__gdCartProcessing = false;
    }
  }

  // Initial sanitize on page load — handles refresh-with-orphan-cart cases
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => debouncedSanitize(400));
  } else {
    debouncedSanitize(400);
  }

  // Cart-event triggers
  document.body.addEventListener('cart:updated', () => debouncedSanitize(180));
  document.body.addEventListener('product-added-to-cart', () => debouncedSanitize(180));
  document.body.addEventListener('gd:cart-refreshed', () => debouncedSanitize(180));

  // fetch monkey-patch — same pattern as the GWP reconciler. Catches
  // direct /cart/(add|change|update).js POSTs from any source (Dawn
  // forms, gd-cart-add.js, kit-builder, instant-gels-builder, AND
  // direct DevTools-issued attacks). The processing flag prevents
  // recursion when sanitize()'s own /cart/change.js calls re-trigger.
  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const result = originalFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update)\.js/.test(url)) {
      // Slight delay so the cart-add response has settled before we read.
      result.then(() => debouncedSanitize(220)).catch(() => {});
    }
    return result;
  };
})();
